import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { TimerUtils } from "abweb/utils/TimerUtils";
/**
 * Manages keyboard interaction in DataGrid
 */
export class DataGridKeyboardMgr {
  constructor(dataGrid, selectionMgr) {
    _defineProperty(this, "dataGrid", void 0);
    _defineProperty(this, "selectionMgr", void 0);
    _defineProperty(this, "debounceTimerId", null);
    _defineProperty(this, "debounceTimerInterval", 75);
    this.dataGrid = dataGrid;
    this.selectionMgr = selectionMgr;
  }
  onKeyDown(event) {
    let handled = false;
    if (this.filterKeyWhileEditing(event)) {
      return;
    }
    switch (event.key) {
      case KeyValue.ARROW_DOWN:
        handled = this.handleArrowDown(event);
        break;
      case KeyValue.ARROW_UP:
        handled = this.handleArrowUp(event);
        break;
      case KeyValue.ARROW_LEFT:
        handled = this.handleArrowLeft(event);
        break;
      case KeyValue.ARROW_RIGHT:
        handled = this.handleArrowRight(event);
        break;
      case KeyValue.TAB:
        handled = this.handleTab(event);
        break;
      case KeyValue.HOME:
        handled = this.handleHome(event);
        break;
      case KeyValue.END:
        handled = this.handleEnd(event);
        break;
      case KeyValue.PAGE_DOWN:
        handled = this.handlePageDown(event);
        break;
      case KeyValue.PAGE_UP:
        handled = this.handlePageUp(event);
        break;
      case KeyValue.F2:
        handled = this.handleF2(event);
        break;
      case KeyValue.ESCAPE:
        handled = this.handleEscape(event);
        break;
      case KeyValue.ENTER:
        handled = this.handleEnter(event);
        break;
      // TODO: And more keys...
    }
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
  handleArrowDown(event) {
    let selectionRegion = this.selectionMgr.selectionRegion;
    let focusCell = selectionRegion.focusCell;
    let anchorItem = selectionRegion.anchorItem;
    let anchorItem2 = selectionRegion.anchorItem2;
    let extendSelection = event.shiftKey;
    if (focusCell) {
      // We have a focusCell
      let targetCell = extendSelection && anchorItem2 && anchorItem2.isCell ? anchorItem2 : focusCell;
      let rowIndex = this.dataGrid.getRowIndexFromId(targetCell.rowId);
      if (rowIndex === this.dataGrid.props.rowCount - 1) {
        return false;
      }
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex + 1);
      if (rowId == null) {
        // Already at bottom
        return false;
      }

      // DataGridKeyboardMgr.LOGGER.info(`handleArrowDown repeat: ${event.repeat}`);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, targetCell.colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, targetCell.colId, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isRow) {
      // Anchor item is row
      let targetItem = extendSelection && anchorItem2 && anchorItem2.isRow ? anchorItem2 : anchorItem;
      // so move the row selection down by 1 row
      let rowIndex = this.dataGrid.getRowIndexFromId(targetItem.rowId);
      if (rowIndex === this.dataGrid.props.rowCount - 1) {
        return false;
      }
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex + 1);
      if (rowId == null) {
        // Already at bottom
        return false;
      }
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, null, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, null, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isColumn && this.selectionMgr.selectionRegion.isOnlyItemSelected(null, anchorItem.colId)) {
      // We have only 1 column selected
      // Select first cell in column
      let rowId = this.dataGrid.getRowIdFromIndex(0);
      this.selectionMgr.selectCell(rowId, anchorItem.colId, false, true);
      this.dataGrid.scrollToCell({
        rowId: rowId,
        colId: anchorItem.colId
      });
      return true;
    }
    return false;
  }
  handleArrowUp(event) {
    let selectionRegion = this.selectionMgr.selectionRegion;
    let focusCell = selectionRegion.focusCell;
    let anchorItem = selectionRegion.anchorItem;
    let anchorItem2 = selectionRegion.anchorItem2;
    let extendSelection = event.shiftKey;
    if (focusCell) {
      // We have a focusCell
      let targetCell = extendSelection && anchorItem2 && anchorItem2.isCell ? anchorItem2 : focusCell;
      let rowIndex = this.dataGrid.getRowIndexFromId(targetCell.rowId);
      if (rowIndex === 0) {
        // We are at top
        // so interpret this to mean select the column
        this.selectionMgr.selectColumn(targetCell.colId, false);
        return true;
      }
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex - 1);
      if (rowId == null) {
        // Already at top
        return false;
      }
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, targetCell.colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, targetCell.colId, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isRow) {
      // Anchor item is row
      let targetItem = extendSelection && anchorItem2 && anchorItem2.isRow ? anchorItem2 : anchorItem;
      // so move the row selection up by 1 row
      let rowIndex = this.dataGrid.getRowIndexFromId(targetItem.rowId);
      if (rowIndex === 0) {
        return false;
      }
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex - 1);
      if (rowId == null) {
        // Already at top
        return false;
      }
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, null, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, null, extendSelection);
      return true;
    }
    return false;
  }
  handleArrowLeft(event) {
    let selectionRegion = this.selectionMgr.selectionRegion;
    let focusCell = selectionRegion.focusCell;
    let anchorItem = selectionRegion.anchorItem;
    let anchorItem2 = selectionRegion.anchorItem2;
    let extendSelection = event.shiftKey;
    if (focusCell) {
      // We have a focusCell
      let targetCell = extendSelection && anchorItem2 && anchorItem2.isCell ? anchorItem2 : focusCell;
      let colIndex = this.dataGrid.getColumnIndexFromId(targetCell.colId);
      if (colIndex === 0) {
        // Move selection to whole row
        this.selectionMgr.selectRow(targetCell.rowId, false);
        return true;
      }
      let rowId = targetCell.rowId;
      let colId = this.dataGrid.getColumnIdFromIndex(colIndex - 1);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, colId, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isColumn && anchorItem2 && anchorItem2.isColumn) {
      // Anchor items are columns
      let targetItem = anchorItem2;
      // Move selection left by 1 column
      let colIndex = this.dataGrid.getColumnIndexFromId(targetItem.colId);
      if (colIndex === 0) {
        // No-op
        return true;
      }
      let colId = this.dataGrid.getColumnIdFromIndex(colIndex - 1);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(null, colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(null, colId, extendSelection);
      return true;
    }
    return false;
  }
  handleArrowRight(event) {
    let selectionRegion = this.selectionMgr.selectionRegion;
    let focusCell = selectionRegion.focusCell;
    let anchorItem = selectionRegion.anchorItem;
    let anchorItem2 = selectionRegion.anchorItem2;
    let extendSelection = event.shiftKey;
    const leafColumnsCount = this.dataGrid.getLeafColumnsCount();
    if (focusCell) {
      // We have a focusCell
      let targetCell = extendSelection && anchorItem2 && anchorItem2.isCell ? anchorItem2 : focusCell;
      let colIndex = this.dataGrid.getColumnIndexFromId(targetCell.colId);
      if (colIndex >= leafColumnsCount - 1) {
        // No-op
        // TODO: Revisit when we support hidden columns

        return true;
      }
      let rowId = targetCell.rowId;
      let colId = this.dataGrid.getColumnIdFromIndex(colIndex + 1);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, colId, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isColumn && anchorItem2 && anchorItem2.isColumn) {
      // Anchor item is column
      let targetItem = anchorItem2;
      // Move selection right by 1 column
      let colIndex = this.dataGrid.getColumnIndexFromId(targetItem.colId);
      if (colIndex >= leafColumnsCount - 1) {
        // No-op
        // TODO: Revisit when we support hidden columns

        return true;
      }
      let colId = this.dataGrid.getColumnIdFromIndex(colIndex + 1);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(null, colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(null, colId, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isRow && this.selectionMgr.selectionRegion.isOnlyItemSelected(anchorItem.rowId, anchorItem.colId)) {
      // We have only 1 row selected
      // so move the selection into the first cell in the row
      let rowId = anchorItem.rowId;
      let colId = this.dataGrid.getColumnIdFromIndex(0);
      this.selectAndScrollTo(rowId, colId, extendSelection);
      return true;
    }
    return false;
  }
  handleTab(event) {
    let focusCell = this.selectionMgr.selectionRegion.focusCell;
    const leafColumnsCount = this.dataGrid.getLeafColumnsCount();
    if (focusCell) {
      // We have a focusCell
      let rowId = focusCell.rowId;
      let colIndex = this.dataGrid.getColumnIndexFromId(focusCell.colId);
      if (event.shiftKey) {
        // Moving left
        if (--colIndex < 0) {
          // Move up 1 row
          colIndex = leafColumnsCount - 1;
          let rowIndex = this.dataGrid.getRowIndexFromId(rowId) - 1;
          rowId = this.dataGrid.getRowIdFromIndex(rowIndex);
          if (rowId == null) {
            return true;
          }
        }
      } else {
        if (++colIndex >= leafColumnsCount) {
          // We are on last column
          // so go to next row
          // TODO: Revisit when we support hidden columns
          colIndex = 0;
          let rowIndex = this.dataGrid.getRowIndexFromId(rowId) + 1;
          rowId = this.dataGrid.getRowIdFromIndex(rowIndex);
          if (rowId == null) {
            return true;
          }
        }
      }
      let colId = this.dataGrid.getColumnIdFromIndex(colIndex);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, colId, false));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, colId, false);
      return true;
    }
    return false;
  }
  handleHome(event) {
    let focusCell = this.selectionMgr.selectionRegion.focusCell;

    // TODO: Revisit when we support hidden columns
    if (event.ctrlKey) {
      // Go to first cell in grid
      let rowId = this.dataGrid.getRowIdFromIndex(0);
      let colId = this.dataGrid.getColumnIdFromIndex(0);
      this.selectAndScrollTo(rowId, colId, event.shiftKey);
      return true;
    } else if (focusCell) {
      // We have a focusCell
      // target cell
      let rowId = focusCell.rowId;
      let colId = this.dataGrid.getColumnIdFromIndex(0);
      this.selectAndScrollTo(rowId, colId, event.shiftKey);
      return true;
    }
    return false;
  }
  handleEnd(event) {
    let focusCell = this.selectionMgr.selectionRegion.focusCell;
    const leafColumnsCount = this.dataGrid.getLeafColumnsCount();

    // TODO: Revisit when we support hidden columns
    if (event.ctrlKey) {
      // Go to last cell in grid
      let rowId = this.dataGrid.getRowIdFromIndex(this.dataGrid.props.rowCount - 1);
      let colId = this.dataGrid.getColumnIdFromIndex(leafColumnsCount - 1);
      this.selectAndScrollTo(rowId, colId, event.shiftKey);
      return true;
    } else if (focusCell) {
      // We have a focusCell
      // target cell
      let rowId = focusCell.rowId;
      let colId = this.dataGrid.getColumnIdFromIndex(leafColumnsCount - 1);
      this.selectAndScrollTo(rowId, colId, event.shiftKey);
      return true;
    }
    return false;
  }
  handlePageDown(event) {
    let selectionRegion = this.selectionMgr.selectionRegion;
    let focusCell = selectionRegion.focusCell;
    let anchorItem = selectionRegion.anchorItem;
    let anchorItem2 = selectionRegion.anchorItem2;
    let extendSelection = event.shiftKey;
    let viewRowCount = this.getViewRowCount();
    if (focusCell) {
      // We have a focusCell
      let targetCell = extendSelection && anchorItem2 && anchorItem2.isCell ? anchorItem2 : focusCell;
      let rowIndex = this.dataGrid.getRowIndexFromId(targetCell.rowId);
      if (rowIndex >= this.dataGrid.props.rowCount - 1) {
        // Already at bottom
        return false;
      }
      rowIndex = Math.min(rowIndex + viewRowCount, this.dataGrid.props.rowCount - 1);
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex);
      if (rowId == null) {
        // Already at bottom
        return false;
      }

      // DataGridKeyboardMgr.LOGGER.info(`handleArrowDown repeat: ${event.repeat}`);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, targetCell.colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, targetCell.colId, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isRow) {
      // Anchor item is row
      let targetItem = extendSelection && anchorItem2 && anchorItem2.isRow ? anchorItem2 : anchorItem;
      // so move the row selection down by viewRowCount rows
      let rowIndex = this.dataGrid.getRowIndexFromId(targetItem.rowId);
      if (rowIndex >= this.dataGrid.props.rowCount - 1) {
        // Already at bottom
        return false;
      }
      rowIndex = Math.min(rowIndex + viewRowCount, this.dataGrid.props.rowCount - 1);
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex);
      if (rowId == null) {
        // Already at bottom
        return false;
      }
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, null, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, null, extendSelection);
      return true;
    }
    return false;
  }
  handlePageUp(event) {
    let selectionRegion = this.selectionMgr.selectionRegion;
    let focusCell = selectionRegion.focusCell;
    let anchorItem = selectionRegion.anchorItem;
    let anchorItem2 = selectionRegion.anchorItem2;
    let extendSelection = event.shiftKey;
    let viewRowCount = this.getViewRowCount();
    if (focusCell) {
      // We have a focusCell
      let targetCell = extendSelection && anchorItem2 && anchorItem2.isCell ? anchorItem2 : focusCell;
      let rowIndex = this.dataGrid.getRowIndexFromId(targetCell.rowId);
      if (rowIndex === 0) {
        // Already at top
        return false;
      }
      rowIndex = Math.max(rowIndex - viewRowCount, 0);
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex);
      if (rowId == null) {
        // Already at top
        return false;
      }

      // DataGridKeyboardMgr.LOGGER.info(`handleArrowDown repeat: ${event.repeat}`);
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, targetCell.colId, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, targetCell.colId, extendSelection);
      return true;
    } else if (anchorItem && anchorItem.isRow) {
      // Anchor item is row
      let targetItem = extendSelection && anchorItem2 && anchorItem2.isRow ? anchorItem2 : anchorItem;
      // so move the row selection up by viewRowCount rows
      let rowIndex = this.dataGrid.getRowIndexFromId(targetItem.rowId);
      if (rowIndex === 0) {
        // Already at top
        return false;
      }
      rowIndex = Math.max(rowIndex - viewRowCount, 0);
      let rowId = this.dataGrid.getRowIdFromIndex(rowIndex);
      if (rowId == null) {
        // Already at top
        return false;
      }
      if (event.repeat) {
        if (this.debounceTimerId == null) {
          this.debouncer(() => this.selectAndScrollTo(rowId, null, extendSelection));
        }
        return true;
      }
      this.selectAndScrollTo(rowId, null, extendSelection);
      return true;
    }
    return false;
  }
  getViewRowCount() {
    // Per ABSDK-5243, seems that there is an edge case with an initially empty grid where the sectionRenderedParams or rowStopIndex, rowStartIndex may be undefined
    let sectionRenderedParams = this.dataGrid.sectionRenderedParams;
    if (sectionRenderedParams?.rowStopIndex === undefined || sectionRenderedParams?.rowStartIndex === undefined) {
      return 0;
    }
    return Math.max(sectionRenderedParams.rowStopIndex - sectionRenderedParams.rowStartIndex, 1);
  }
  handleF2(event) {
    let focusCell = this.dataGrid.focusCell;
    if (focusCell) {
      if (this.dataGrid.isEditing) {
        this.dataGrid.endEdit(false);
      } else {
        this.dataGrid.editCell(focusCell.rowId, focusCell.colId);
      }
    }
    return true;
  }
  handleEscape(event) {
    if (!this.dataGrid.isEditing) {
      return false;
    }
    let focusCell = this.dataGrid.focusCell;
    if (focusCell) {
      this.dataGrid.endEdit(true);
    }
    return true;
  }
  handleEnter(event) {
    let focusCell = this.dataGrid.focusCell;
    if (focusCell) {
      this.dataGrid.endEdit(false);

      // Attempt to set focus up or down in same column
      if (event.shiftKey) {
        event.shiftKey = false; // but we don't want Enter to allow extend of selection region
        return this.handleArrowUp(event);
      } else {
        return this.handleArrowDown(event);
      }
    }
    return true;
  }
  selectAndScrollTo(rowId, colId, extendSelection) {
    let params;
    if (colId == null) {
      // Row select
      if (extendSelection) {
        this.selectionMgr.extendRowSelection(rowId);
      } else {
        this.selectionMgr.selectRow(rowId, false);
      }
      params = {
        rowId
      };
    } else if (rowId == null) {
      // Column select
      if (extendSelection) {
        this.selectionMgr.extendColumnSelection(colId);
      } else {
        this.selectionMgr.selectColumn(colId, false);
      }
      params = {
        colId
      };
    } else {
      if (extendSelection) {
        this.selectionMgr.extendCellSelection(rowId, colId);
      } else {
        this.selectionMgr.selectCell(rowId, colId, false, true);
      }
      params = {
        rowId,
        colId
      };
    }
    this.dataGrid.scrollToCell(params);
  }
  debouncer(callback) {
    if (this.debounceTimerId == null) {
      this.debounceTimerId = TimerUtils.requestAnimationTimeout(() => {
        this.debounceTimerId = null;
        callback();
      }, this.debounceTimerInterval);
    }
  }

  /**
   * If editing, we want to ignore our "normal" processing of some keys.
   * This method determines which key values should be filtered while editing.
   * @param {React.KeyboardEvent<HTMLElement>} event
   * @returns {boolean}
   */
  filterKeyWhileEditing(event) {
    if (!this.dataGrid.isEditing) {
      return false;
    }
    switch (event.key) {
      // Ignore these keys
      case KeyValue.ARROW_UP:
      case KeyValue.ARROW_DOWN:
      case KeyValue.ARROW_RIGHT:
      case KeyValue.ARROW_LEFT:
      case KeyValue.HOME:
      case KeyValue.END:
      case KeyValue.PAGE_DOWN:
      case KeyValue.PAGE_UP:
        return true;
      default:
        return false;
    }
  }
}
// @ts-ignore: noUnusedLocals
_defineProperty(DataGridKeyboardMgr, "LOGGER", LogUtils.getLogger(__filename));
//# sourceMappingURL=DataGridKeyboardMgr.js.map
